import { AceParagraph } from '@/kontent/content-types'
import { RichtextSectionProps } from '.'
import RichText from '../RichText'

/**
 * Adds `start` attribute to top-level <ol> elements so numbering
 * continues across multiple <ol> blocks.
 * Fallback for Safari which does not support CSS `counter-set`.
 */
function addOlStartAttributes(html: string, initialNumber: number): string {
	if (!html) return html

	let currentStart = initialNumber || 1
	let olDepth = 0
	let liCount = 0
	let result = ''
	let i = 0

	while (i < html.length) {
		if (html[i] === '<' && html.substring(i, i + 3) === '<ol') {
			const nextChar = html[i + 3]
			if (nextChar === '>' || nextChar === ' ') {
				const closePos = html.indexOf('>', i)
				if (closePos === -1) {
					result += html[i]
					i++
					continue
				}

				olDepth++
				if (olDepth === 1) {
					const existingAttrs = html.substring(i + 3, closePos)
					result += `<ol start="${currentStart}"${existingAttrs}>`
					liCount = 0
				} else {
					result += html.substring(i, closePos + 1)
				}
				i = closePos + 1
				continue
			}
		}

		if (html[i] === '<' && html.substring(i, i + 5) === '</ol>') {
			if (olDepth === 1) {
				currentStart += liCount
			}
			olDepth--
			result += '</ol>'
			i += 5
			continue
		}

		if (
			olDepth === 1 &&
			html[i] === '<' &&
			html.substring(i, i + 3) === '<li'
		) {
			const nextChar = html[i + 3]
			if (nextChar === '>' || nextChar === ' ') {
				liCount++
			}
		}

		result += html[i]
		i++
	}

	return result
}

export const getAceCustomOrderedListCss = (
	counterKey: string,
	initialNumber = 0,
) => {
	const level2Key = counterKey + 'level2'
	const level3Key = counterKey + 'level3'
	const level4Key = counterKey + 'level4'

	return {
		'li::marker': {
			fontVariantNumeric: 'initial',
		},
		'& > ol': {
			counterSet: `${counterKey}${
				initialNumber ? ` ${initialNumber - 1}` : ''
			}`,
			'> li': {
				counterIncrement: counterKey,
				'&::marker': {
					content: `counters(${counterKey}, "", decimal) ". "`,
				},
			},
		},
		'& ol ol': {
			listStyle: 'lower-alpha',
			counterSet: `${level2Key} 0`,
			'> li': {
				counterIncrement: level2Key,
			},
		},
		'& ol ol ol': {
			listStyle: 'lower-roman',
			counterSet: `${level3Key} 0`,
			'> li': {
				counterIncrement: level3Key,
			},
		},
		'& ol ol ol ol': {
			listStyle: 'decimal',
			paddingLeft: '1.875rem',
			counterSet: level4Key,
			li: {
				counterIncrement: level4Key,
			},
			'li::marker': {
				content: `"(" counters(${level4Key}, "", decimal) ") "`,
			},
		},
	}
}

export default function AceParagraphComponent({
	linkedItem,
	mappings,
	linkedItems,
	currentPath,
	currentStage,
	currentYear,
	resolveFootnotesLink,
}: RichtextSectionProps<AceParagraph>) {
	const initialNumber = linkedItem.elements.orderedliststartfrom.value
	const processedContent = {
		...linkedItem.elements.content,
		value: addOlStartAttributes(
			linkedItem.elements.content.value,
			initialNumber,
		),
	}

	return (
		<RichText
			css={getAceCustomOrderedListCss(
				linkedItem.system.codename,
				initialNumber,
			)}
			richTextElement={processedContent}
			linkedItems={linkedItems}
			mappings={mappings}
			currentPath={currentPath}
			currentStage={currentStage}
			currentYear={currentYear}
			resolveFootnotesLink={resolveFootnotesLink}
		/>
	)
}
